import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Camera, Instagram, Youtube, Facebook, Music2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { updateClientFullProfile } from '../lib/api';
import { getInitials } from '../lib/utils';
import ImageCropper from './ImageCropper';

const EditClientProfileDialog = ({ open, onOpenChange }) => {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    const p = user.client_profile || {};
    setForm({
      full_name: user.full_name || '',
      phone: user.phone || '',
      city: user.city || '',
      address: user.address || '',
      bio: p.bio || '',
      preferred_times: p.preferred_times || '',
      profile_image: p.profile_image || '',
      social_instagram: p.social_instagram || '',
      social_tiktok: p.social_tiktok || '',
      social_youtube: p.social_youtube || '',
      social_facebook: p.social_facebook || '',
    });
  }, [open, user]);

  if (!form) return null;

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const removeImage = () => setField('profile_image', '');

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone?.trim() || null,
        city: form.city?.trim() || null,
        address: form.address?.trim() || null,
        client_profile: {
          bio: form.bio || null,
          preferred_times: form.preferred_times?.trim() || null,
          profile_image: form.profile_image || null,
          social_instagram: form.social_instagram?.trim() || null,
          social_tiktok: form.social_tiktok?.trim() || null,
          social_youtube: form.social_youtube?.trim() || null,
          social_facebook: form.social_facebook?.trim() || null,
        },
      };
      await updateClientFullProfile(payload);
      await refreshUser();
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#18181B] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-client-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Edit profile</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Update your profile photo, bio, and social links.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile picture */}
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 border-2 border-[#C0A062]/40">
              <AvatarImage src={form.profile_image} alt={form.full_name} />
              <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xl font-bold">
                {getInitials(form.full_name || 'C')}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  onClick={() => setCropperOpen(true)}
                  data-testid="client-upload-image-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {form.profile_image ? 'Change photo' : 'Upload photo'}
                </Button>
                {form.profile_image && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                    onClick={removeImage}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-zinc-500">Any size accepted, we&apos;ll auto-crop &amp; compress.</p>
            </div>
          </div>

          {/* Name + Location */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="client_full_name" className="text-zinc-300">Full name</Label>
              <Input
                id="client_full_name"
                value={form.full_name}
                onChange={(e) => setField('full_name', e.target.value)}
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="client-input-full-name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="client_city" className="text-zinc-300">City</Label>
              <Input
                id="client_city"
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                placeholder="e.g. Brooklyn, NY"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="client-input-city"
              />
            </div>
          </div>

          {/* Bio */}
          <div className="grid gap-2">
            <Label htmlFor="client_bio" className="text-zinc-300">Bio / About me</Label>
            <Textarea
              id="client_bio"
              rows={4}
              value={form.bio}
              onChange={(e) => setField('bio', e.target.value)}
              placeholder="Tell trainers about your goals, experience, and what motivates you."
              className="bg-[#09090B] border-zinc-800 text-white"
              data-testid="client-input-bio"
            />
          </div>

          {/* Phone */}
          <div className="grid gap-2">
            <Label htmlFor="client_phone" className="text-zinc-300">Phone</Label>
            <Input
              id="client_phone"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="bg-[#09090B] border-zinc-800 text-white"
              data-testid="client-input-phone"
            />
          </div>

          {/* Home address + preferred times */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="client_address" className="text-zinc-300">Home address / area</Label>
              <Input
                id="client_address"
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                placeholder="Street address or neighborhood"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="client-input-address"
              />
              <p className="text-[11px] text-zinc-500">Only shared with a coach after a session is confirmed.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="client_preferred_times" className="text-zinc-300">Preferred session times</Label>
              <Input
                id="client_preferred_times"
                value={form.preferred_times}
                onChange={(e) => setField('preferred_times', e.target.value)}
                placeholder="e.g. Weekday evenings, Sat mornings"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="client-input-preferred-times"
              />
            </div>
          </div>

          {/* Social links */}
          <div className="grid gap-3">
            <Label className="text-zinc-300">Social links (optional)</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_instagram}
                  onChange={(e) => setField('social_instagram', e.target.value)}
                  placeholder="Instagram URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="client-input-instagram"
                />
              </div>
              <div className="relative">
                <Music2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_tiktok}
                  onChange={(e) => setField('social_tiktok', e.target.value)}
                  placeholder="TikTok URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="client-input-tiktok"
                />
              </div>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_youtube}
                  onChange={(e) => setField('social_youtube', e.target.value)}
                  placeholder="YouTube URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="client-input-youtube"
                />
              </div>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_facebook}
                  onChange={(e) => setField('social_facebook', e.target.value)}
                  placeholder="Facebook URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="client-input-facebook"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
            data-testid="client-save-profile-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
      <ImageCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        onCropped={(dataUrl) => setField('profile_image', dataUrl)}
        title="Crop your profile photo"
      />
    </Dialog>
  );
};

export default EditClientProfileDialog;
